'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface FilterOption {
  id: string;
  label: string;
  count: number;
}

interface FilterBarProps {
  industries: FilterOption[];
  companySizes: FilterOption[];
  serviceTypes: FilterOption[];
  selectedIndustry: string;
  selectedCompanySize: string;
  selectedServiceType: string;
  searchQuery: string;
  onIndustryChange: (value: string) => void;
  onCompanySizeChange: (value: string) => void;
  onServiceTypeChange: (value: string) => void;
  onSearchChange: (value: string) => void;
  onClearFilters: () => void;
  className?: string;
}

const FilterBar = ({
  industries,
  companySizes,
  serviceTypes,
  selectedIndustry,
  selectedCompanySize,
  selectedServiceType,
  searchQuery,
  onIndustryChange,
  onCompanySizeChange,
  onServiceTypeChange,
  onSearchChange,
  onClearFilters,
  className = ''
}: FilterBarProps) => {
  const hasActiveFilters = selectedIndustry !== 'all' || selectedCompanySize !== 'all' || selectedServiceType !== 'all' || searchQuery !== '';

  return (
    <div className={`bg-card rounded-lg shadow-subtle p-6 ${className}`}>
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <h2 className="text-xl font-display font-semibold text-foreground">Filter Case Studies</h2>
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="flex items-center space-x-2 text-sm text-accent hover:text-accent/80 transition-colors duration-300"
          >
            <Icon name="XMarkIcon" size={16} />
            <span>Clear All Filters</span>
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Search */}
        <div className="relative">
          <Icon name="MagnifyingGlassIcon" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search case studies..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
          />
        </div>

        {/* Industry Filter */}
        <div className="relative">
          <select
            value={selectedIndustry}
            onChange={(e) => onIndustryChange(e.target.value)}
            className="w-full px-4 py-2.5 bg-background border border-border rounded-md text-sm text-foreground appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
          >
            {industries.map((industry) => (
              <option key={industry.id} value={industry.id}>
                {industry.label} ({industry.count})
              </option>
            ))}
          </select>
          <Icon name="ChevronDownIcon" size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        </div>

        {/* Company Size Filter */}
        <div className="relative">
          <select
            value={selectedCompanySize}
            onChange={(e) => onCompanySizeChange(e.target.value)}
            className="w-full px-4 py-2.5 bg-background border border-border rounded-md text-sm text-foreground appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
          >
            {companySizes.map((size) => (
              <option key={size.id} value={size.id}>
                {size.label} ({size.count})
              </option>
            ))}
          </select>
          <Icon name="ChevronDownIcon" size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        </div>

        {/* Service Type Filter */}
        <div className="relative">
          <select
            value={selectedServiceType}
            onChange={(e) => onServiceTypeChange(e.target.value)}
            className="w-full px-4 py-2.5 bg-background border border-border rounded-md text-sm text-foreground appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
          >
            {serviceTypes.map((service) => (
              <option key={service.id} value={service.id}>
                {service.label} ({service.count})
              </option>
            ))}
          </select>
          <Icon name="ChevronDownIcon" size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        </div>
      </div>
    </div>
  );
};

export default FilterBar;